/**
 * Answers one question: is the signed-in person an admin?
 *
 * The browser is never trusted for this. We ask the API (GET /me), which checks
 * the Clerk token against ADMIN_EMAILS and Clerk metadata. The result only
 * decides whether admin buttons are *shown* — the API re-checks every write.
 */

import { useAuth } from "@clerk/clerk-react";
import { useEffect, useState } from "react";

import { apiGet } from "../lib/api";
import type { Viewer } from "../types";

export function useViewer() {
  const { isSignedIn, getToken } = useAuth();
  const [viewer, setViewer] = useState<Viewer>({ isAdmin: false, role: "user" });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isSignedIn) {
      setViewer({ isAdmin: false, role: "user" });
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    getToken()
      .then((token) => apiGet<Viewer>("/me", { token }))
      .then((result) => {
        if (!cancelled) setViewer(result);
      })
      .catch(() => {
        if (!cancelled) setViewer({ isAdmin: false, role: "user" });
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isSignedIn, getToken]);

  return { ...viewer, isSignedIn: Boolean(isSignedIn), isLoading };
}
